import { Chip, Grid, Tooltip } from '@mui/material';
import { CustomFieldDefinition, CustomFieldValues } from '@nx-smartmonkey/shared/domain';
import { calculateDuration, dateFormat, parsePhoneNumber, parseSecondsToHM } from '@nx-smartmonkey/shared/helpers';
import { TimeWindow } from '@nx-smartmonkey/shared/interfaces';
import { RoutalPalette, RoutalTooltip } from '@nx-smartmonkey/ui';

import More3Points from '../../../../assets/svg/More3Points';
import { customFieldToTableValue } from '../../../../components/TableColumnsButton';
import { CUSTOM_FIELD_COLUMN_ID_PREFIX } from '../../../../constants';
import { ClientsTableColumn, ClientsTableColumnKeys } from '../../../../context/client/tableColumns';
import { Client } from '../../../../domain/client/Client';
import { Project } from '../../../../domain/project/Project';
import { intl } from '../../../../helpers/IntGlobalProvider';
import Text from '../../../../react-components/Text';
import { AlignmentTypes, DataTableColumnProps } from '../../../../react-components/data-table/DataTable';
import { ClientsActionsMenu } from '../ClientsActionsMenu';

type ClientsGetColumnsResponse = { [key: string]: any }[];

type ClientsGetColumns = {
  activeProject: Project;
  tableColumns: Array<ClientsTableColumn>;
  onOpenRightPanel?: (...args: any[]) => any;
};

export const getClientsColumns = ({
  activeProject,
  tableColumns,
  onOpenRightPanel,
}: ClientsGetColumns): ClientsGetColumnsResponse => {
  const columns: Array<DataTableColumnProps> = [];

  const clientCustomFieldDefinitionsMap: Record<string, CustomFieldDefinition> = (
    activeProject.custom_fields?.client ?? []
  )
    .filter((customFieldDefinition) => customFieldDefinition.enabled)
    .reduce((amount, customFieldDefinition) => {
      return {
        ...amount,
        [`${CUSTOM_FIELD_COLUMN_ID_PREFIX}${customFieldDefinition.custom_id}`]: customFieldDefinition,
      };
    }, {});

  tableColumns.forEach(({ displayable, identifier, sortable }: ClientsTableColumn) => {
    if (displayable) {
      switch (identifier) {
        case `label`:
          columns.push({
            width: 250,
            accessorName: `label`,
            sortable,
            name: <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="clients.data.details.label" />,
            formatter: ({ value: label }) => (
              <Grid container direction="row" alignItems="center">
                {label && label !== `` ? (
                  <RoutalTooltip title={label}>
                    <Text variant="body">{label.length > 30 ? `${label.substring(0, 30)}...` : `${label}`}</Text>
                  </RoutalTooltip>
                ) : (
                  <Text variant="body" style={{ color: RoutalPalette.secondary00 }} intlId="no_name" />
                )}
              </Grid>
            ),
          });
          break;
        case `location`:
          columns.push({
            width: 240,
            accessorName: `location`,
            sortable,
            name: <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="clients.data.details.location" />,
            formatter: ({ value: location }) =>
              location && (
                <RoutalTooltip title={location?.label ?? ``}>
                  <Text variant="body" style={{ maxWidth: `240px` }}>
                    {location.label}
                  </Text>
                </RoutalTooltip>
              ),
          });
          break;
        case `default_duration`:
          columns.push({
            width: 80,
            accessorName: `default_duration`,
            align: `center` as AlignmentTypes,
            sortable,
            name: (
              <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="clients.data.details.default_duration" />
            ),
            formatter: ({ value: defaultDuration }) => (
              <Text variant="body">{`${calculateDuration(defaultDuration)} min`}</Text>
            ),
          });
          break;
        case `default_time_windows`:
          if (activeProject?.view?.constraints?.time_windows) {
            columns.push({
              width: 220,
              accessorName: `default_time_windows`,
              align: `center` as AlignmentTypes,
              name: (
                <Text
                  style={{ color: RoutalPalette.primary20 }}
                  isBold
                  intlId="clients.data.details.default_time_windows"
                />
              ),
              formatter: ({ doc: client }) => {
                if (client.default_time_windows && client.default_time_windows.length > 0) {
                  return client.default_time_windows.length > 2 ? (
                    <Grid container alignItems="center" justifyContent="center">
                      <Chip
                        key={client.default_time_windows[0][0] + client.default_time_windows[0][1]}
                        label={
                          <Text variant="body">{`${parseSecondsToHM(
                            client.default_time_windows[0][0]
                          )}-${parseSecondsToHM(client.default_time_windows[0][1])}`}</Text>
                        }
                      />
                      <span
                        style={{
                          cursor: `pointer`,
                          color: RoutalPalette.primary40,
                          fontFamily: `Geist Variable`,
                          marginLeft: `4px`,
                          border: `none`,
                        }}
                        onClick={(e) => {
                          if (onOpenRightPanel) {
                            onOpenRightPanel(e, client);
                          }
                        }}
                      >
                        <Text tagName="span" intlId="view_more" />
                        ...
                      </span>
                    </Grid>
                  ) : (
                    client.default_time_windows.map((timeWindow: TimeWindow) => (
                      <Chip
                        key={timeWindow[0] + timeWindow[1]}
                        label={
                          <Text variant="body">{`${parseSecondsToHM(timeWindow[0])}-${parseSecondsToHM(
                            timeWindow[1]
                          )}`}</Text>
                        }
                      />
                    ))
                  );
                }
                return null;
              },
            });
          }
          break;
        case `default_weight`:
          if (activeProject?.view?.constraints?.weight) {
            columns.push({
              width: 110,
              accessorName: `default_weight`,
              align: `center` as AlignmentTypes,
              sortable,
              name: (
                <Text variant="label">{`${intl.formatMessage({ id: `clients.data.details.default_weight` })} (${
                  activeProject?.units?.weight
                })`}</Text>
              ),
              formatter: ({ value: defaultWeight }) => <Text variant="body">{defaultWeight ?? `-`}</Text>,
            });
          }
          break;
        case `default_volume`:
          if (activeProject?.view?.constraints?.volume) {
            columns.push({
              width: 110,
              accessorName: `default_volume`,
              align: `center` as AlignmentTypes,
              sortable,
              name: (
                <Text variant="label">{`${intl.formatMessage({
                  id: `clients.data.details.default_volume`,
                })} (${activeProject?.units?.volume})`}</Text>
              ),
              formatter: ({ value: defaultVolume }) => (
                <Text variant="body">{defaultVolume !== undefined ? defaultVolume : `-`}</Text>
              ),
            });
          }
          break;
        case `default_requires`:
          columns.push({
            width: 220,
            align: `center` as AlignmentTypes,
            accessorName: `default_requires`,
            name: (
              <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="clients.data.details.default_requires" />
            ),
            formatter: ({ doc: client }) => {
              if (client.default_requires && client.default_requires.length > 0) {
                return client.default_requires.length > 2 ? (
                  <Grid container alignItems="center" justifyContent="center">
                    <Tooltip
                      disableHoverListener={client.default_requires[0].length <= 8}
                      title={client.default_requires[0]}
                    >
                      <Chip
                        key={client.default_requires[0]}
                        label={
                          <Text variant="body">{`${client.default_requires[0].substring(0, 8)}${
                            client.default_requires[0].length > 8 ? `...` : ``
                          }`}</Text>
                        }
                      />
                    </Tooltip>
                    <span
                      style={{
                        cursor: `pointer`,
                        color: RoutalPalette.primary40,
                        fontFamily: `Geist Variable`,
                        marginLeft: `4px`,
                        border: `none`,
                      }}
                      onClick={(e) => {
                        if (onOpenRightPanel) {
                          onOpenRightPanel(e, client);
                        }
                      }}
                    >
                      <Text tagName="span" intlId="view_more" />
                      ...
                    </span>
                  </Grid>
                ) : (
                  client.default_requires.map((require: string) => (
                    <Tooltip disableHoverListener={require.length <= 8} title={require}>
                      <Chip
                        key={require}
                        label={
                          <Text variant="body">{`${require.substring(0, 8)}${require.length > 8 ? `...` : ``}`}</Text>
                        }
                      />
                    </Tooltip>
                  ))
                );
              }
              return null;
            },
          });
          break;
        case `default_cluster`:
          if (activeProject?.view?.constraints?.cluster) {
            columns.push({
              width: 130,
              accessorName: `default_cluster`,
              align: `center` as AlignmentTypes,
              sortable,
              name: (
                <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="clients.data.details.default_cluster" />
              ),
            });
          }
          break;
        case `external_id`:
        case `location_details`:
        case `comments`:
        case `reference_person`:
        case `phone`:
        case `url`:
        case `email`:
          if (activeProject?.view?.client[identifier]) {
            columns.push({
              width: identifier === `url` || identifier === `phone` ? 120 : 220,
              accessorName: identifier,
              align: `center` as AlignmentTypes,
              sortable,
              name: (
                <Text style={{ color: RoutalPalette.primary20 }} isBold intlId={`clients.data.details.${identifier}`} />
              ),
              ...(identifier === `comments`
                ? {
                    formatter: ({ value: comments }) =>
                      comments && (
                        <RoutalTooltip title={comments}>
                          <Text variant="body" style={{ maxWidth: `220px` }}>
                            {comments}
                          </Text>
                        </RoutalTooltip>
                      ),
                  }
                : {}),
              ...(identifier === `phone`
                ? {
                    formatter: ({ value: phone }) => {
                      if (!phone) return undefined;

                      const parsedPhone = parsePhoneNumber(phone);
                      if (!parsedPhone) return undefined;

                      return <Text variant="body">{parsedPhone.formatInternational()}</Text>;
                    },
                  }
                : {}),
            });
          }
          break;
        case `created_at`:
          break;
        default: {
          if (
            (identifier as ClientsTableColumnKeys).includes(CUSTOM_FIELD_COLUMN_ID_PREFIX) &&
            identifier in clientCustomFieldDefinitionsMap
          ) {
            const customFieldDefinition = clientCustomFieldDefinitionsMap[identifier];
            columns.push({
              width: 160,
              accessorName: identifier,
              sortable,
              align: `center` as AlignmentTypes,
              name: (
                <Text style={{ color: RoutalPalette.primary20 }} isBold>
                  {customFieldDefinition.label}
                </Text>
              ),
              formatter: ({ doc }: { doc: Client }) => {
                if (!doc?.custom_fields) {
                  return null;
                }
                const clientCustomFields = doc.custom_fields.value;
                const customField = clientCustomFields[customFieldDefinition.custom_id] as CustomFieldValues;

                return customFieldToTableValue({
                  doc,
                  customField,
                  customFieldDefinition,
                  onOpenRightPanel,
                });
              },
            });
          }
          break;
        }
      }
    }
  });

  return [
    ...columns,
    ...(tableColumns.find(({ displayable, identifier }) => identifier === `created_at` && displayable)
      ? [
          {
            width: 130,
            accessorName: `created_at`,
            align: `center` as AlignmentTypes,
            sortable: true,
            name: <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="clients.data.details.created_at" />,
            formatter: ({ value: createdAt }) => <Text variant="body">{dateFormat(createdAt)}</Text>,
          } as DataTableColumnProps,
        ]
      : []),
    {
      align: `right`,
      width: 100,
      flex: 1,
      formatter: ({ doc: client, rowHover }) => (
        <Grid container justifyContent="flex-end">
          {rowHover ? (
            <ClientsActionsMenu
              type="popover"
              clients={[client]}
              popoverProps={{
                hideArrow: true,
                content: <More3Points />,
                style: { height: `18px`, color: RoutalPalette.secondary60, padding: 0, margin: 0, border: `unset` },
              }}
            />
          ) : null}
        </Grid>
      ),
    },
  ] as Array<DataTableColumnProps>;
};
